import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  LinearProgress,
  FormControl,
  Collapse,
  IconButton,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  Popover
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningIcon from '@mui/icons-material/Warning';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ViewIcon from '../../assets/images/View.svg';
import { toast } from "react-toastify";
import { getContractsWithDetails } from '../../api/api';
import frontendSettings from '../../../frontend_settings.json';
import SearchableDropdown from '../../components/SearchableDropdown/SeachableDropdown';
import { useNavigate } from 'react-router-dom';
import RunScenarioModal from '../../components/modals/RunScenarioModal';
import { listScenarios } from '../../api/scenarios';

// Using the existing contract structure from the API response

// Status dot component
const StatusDot = ({ status }: { status: string }) => {
  return (
    <Box
      sx={{
        backgroundColor: status,
        width: 12,
        height: 12,
        borderRadius: '50%',
        display: 'inline-block',
        margin: '0 2px'
      }}
    />
  );
};

// Value Pool Tag component
const ValuePoolTag = ({ category, count, contractId }: { category: string, count: number, contractId: number }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate(`/homepage/scenarios?contract_ids=${contractId}`);
  };
  
  return (
    <Tooltip title={`${count} scenarios - Click to view`} arrow placement="top">
      <Box
        onClick={handleClick}
        sx={{
          backgroundColor: '#f0f0f0',
          borderRadius: '16px',
          padding: '4px 8px',
          margin: '2px',
          display: 'inline-block',
          fontSize: '0.75rem',
          fontWeight: 500,
          color: '#333',
          border: '1px solid #ddd',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#e0e0e0',
            borderColor: '#ccc'
          }
        }}
      >
        {category}
      </Box>
    </Tooltip>
  );
};

// Function to determine the color of each status dot
const getStatusColor = (status: string, dotIndex: number) => {
  const { grey, green, red } = frontendSettings.CWStatus;
  
  if (status === "EMPTY_WORKSPACE") return grey; // All gray

  if (status === "UPLOADED") {
    return dotIndex === 0 ? green : grey; // First dot green
  }

  if (status === "INGESTED" || status === "INGESTION_FAILED") {
    if (dotIndex === 0) return green; // First dot green
    if (dotIndex === 1)
      return status === "INGESTION_FAILED" ? red : green; // Second dot green or red
    return grey; // Third dot grey
  }

  if (status === "ANALYSED") {
    return green; // All dots green
  }

  if (status === "ANALYSIS_FAILED") {
    return dotIndex === 2 ? red : green; // First two dots green, last red
  }

  return grey; // Default to grey
};

const ContractReview: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [contracts, setContracts] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContracts, setSelectedContracts] = useState<string[]>([]);
  const [openAnalysisModal, setOpenAnalysisModal] = useState(false);
  const [openRunScenarioModal, setOpenRunScenarioModal] = useState(false);
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState('');
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState('');
  // Track expanded rows
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  
  // Column visibility settings
  const [columnSettings, setColumnSettings] = useState({
    valuePool: false,  // Hidden by default
    valueLeakage: false  // Hidden by default
  });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLButtonElement | null>(null);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await getContractsWithDetails(searchTerm);
      
      if (response && response.success && response.data && Array.isArray(response.data.contracts)) {
        setContracts(response.data.contracts);
        setTotalItems(response.data.contracts.length);
      } else {
        setContracts([]);
        setTotalItems(0);
      }
    } catch (error) {
      toast.error('Failed to fetch contracts');
      console.error('Error fetching contracts:', error);
      setContracts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [searchTerm]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (id: string) => {
    navigate(`/homepage/management?id=${id}`);
  };

  const navigateToOpportunities = (contractId: number) => {
    navigate(`/homepage/opportunities?contract_ids=${contractId}`);
  };

  const navigateToScenarios = (contractId: number) => {
    navigate(`/homepage/scenarios?contract_ids=${contractId}`);
  };
  
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleSelectContract = (event: React.ChangeEvent<HTMLInputElement>, contractId: string) => {
    if (event.target.checked) {
      setSelectedContracts([...selectedContracts, contractId]);
    } else {
      setSelectedContracts(selectedContracts.filter(id => id !== contractId));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allContractIds = paginatedContracts.map(contract => contract.contract_id.toString());
      setSelectedContracts(allContractIds);
    } else {
      setSelectedContracts([]);
    }
  };

  const handleRunAnalysis = () => {
    if (selectedContracts.length === 0) {
      toast.error('Please select at least one contract');
      return;
    }
    
    // Get the first selected contract for display in the modal
    const firstSelectedContract = contracts.find(contract => 
      selectedContracts.includes(contract.contract_id.toString())
    );
    
    if (firstSelectedContract) {
      setSelectedWorkspaceID(firstSelectedContract.contract_id.toString());
      setSelectedWorkspaceName(firstSelectedContract.contract_workspace);
      setOpenRunScenarioModal(true);
    }
  };
  
  const handleRunAnalysisForContract = (contract: any) => {
    setSelectedWorkspaceID(contract.contract_id.toString());
    setSelectedWorkspaceName(contract.contract_workspace);
    setOpenRunScenarioModal(true);
  };

  const handleCloseModal = () => {
    setOpenAnalysisModal(false);
    setOpenRunScenarioModal(false);
  };

  // Calculate pagination
  const paginatedContracts = contracts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Process contracts for display
  const contractsWithActions = paginatedContracts.map(contract => ({
    ...contract
  }));

  const handleRowClick = (contractId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [contractId]: !prev[contractId]
    }));
  };

  return (
    <Grid container sx={{ width: "100%", justifyContent: "center" }}>
      <Grid
        sx={{
          flex: 1,
          mt: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#fff",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
          borderRadius: "8px",
          width: "100%",
        }}
      >
        <Grid
          container
          sx={{ width: "100%" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            align="center"
            sx={{ fontSize: "1.15rem", px: 2, p: 1.5 }}
          >
            Document Review
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleRunAnalysis}
            sx={{ mr: 2 }}
            disabled={selectedContracts.length === 0}
          >
            Run Analysis {selectedContracts.length > 0 ? `(${selectedContracts.length})` : ''}
          </Button>
        </Grid>
        <Divider sx={{ width: "100%" }} />
        
        {/* Search section */}
        <Box sx={{ width: '100%', p: 2 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              mb: 1
            }}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Typography variant="subtitle1">Search</Typography>
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          
          <Collapse in={filtersExpanded}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControl sx={{ flexGrow: 1, mr: 2 }}>
                  <input
                    type="text"
                    placeholder="Search contracts..."
                    value={searchTerm}
                    onChange={handleSearch}
                    style={{
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      width: '100%',
                      fontSize: '14px'
                    }}
                  />
                </FormControl>
                <Tooltip title="Column Settings">
                  <IconButton 
                    onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Collapse>
          
          {/* Column Settings Popover */}
          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={() => setSettingsAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ p: 2, width: 250 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Column Visibility</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={columnSettings.valuePool}
                    onChange={(e) => setColumnSettings(prev => ({ ...prev, valuePool: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Value Pool"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={columnSettings.valueLeakage}
                    onChange={(e) => setColumnSettings(prev => ({ ...prev, valueLeakage: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Value Leakage"
              />
            </Box>
          </Popover>
        </Box>
        
        {/* Contracts Table */}
        <TableContainer component={Paper} sx={{ width: '100%', mb: 3 }}>
          {loading && <LinearProgress />}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={"6%"} padding="checkbox">
                  <Checkbox
                    indeterminate={selectedContracts.length > 0 && selectedContracts.length < paginatedContracts.length}
                    checked={paginatedContracts.length > 0 && selectedContracts.length === paginatedContracts.length}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'select all contracts' }}
                  />
                </TableCell>
                <TableCell width={columnSettings.valuePool && columnSettings.valueLeakage ? "15%" : "20%"}>Document Workspace</TableCell>
                <TableCell width={columnSettings.valuePool && columnSettings.valueLeakage ? "10%" : "15%"}>Status</TableCell>
                <TableCell width="6%" align="center">Files</TableCell>
                <TableCell width="6%" align="center">Invoices</TableCell>
                {columnSettings.valuePool && <TableCell width="41%">Value Pool</TableCell>}
                <TableCell width={columnSettings.valuePool && columnSettings.valueLeakage ? "8%" : "15%"} align="center">Compliance</TableCell>
                {columnSettings.valueLeakage && <TableCell width="12%" align="center">Value Leakage</TableCell>}
                <TableCell width="2%"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && contractsWithActions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : contractsWithActions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No contracts found
                  </TableCell>
                </TableRow>
              ) : (
                contractsWithActions.map((contract) => (
                  <React.Fragment key={contract.contract_id}>
                    <TableRow 
                      hover
                      onClick={() => handleRowClick(contract.contract_id.toString())}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                        bgcolor: expandedRows[contract.contract_id.toString()] ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedContracts.includes(contract.contract_id.toString())}
                          onChange={(e) => handleSelectContract(e, contract.contract_id.toString())}
                          inputProps={{ 'aria-labelledby': `contract-${contract.contract_id}` }}
                        />
                      </TableCell>
                      <TableCell>{contract.contract_workspace}</TableCell>
                      <TableCell>
                        <Tooltip title={contract.status || 'N/A'} arrow placement="top">
                          <Box display="flex" gap={1}>
                            <StatusDot status={getStatusColor(contract.status, 0)} />
                            <StatusDot status={getStatusColor(contract.status, 1)} />
                            <StatusDot status={getStatusColor(contract.status, 2)} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">{contract.file_count || '0'}</TableCell>
                      <TableCell align="center">{contract.invoice_count || '0'}</TableCell>
                      {columnSettings.valuePool && (
                        <TableCell>
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 0.5, 
                            maxWidth: '100%',
                            overflow: 'hidden'
                          }}>
                            {contract.category_statistics ? 
                              Object.keys(contract.category_statistics)
                                .filter(category => {
                                  const stats = contract.category_statistics[category];
                                  // Only show categories with findings or scenarios > 0
                                  return (stats.findings_count > 0 || stats.scenarios_count > 0);
                                })
                                .map((category) => (
                                  <Box 
                                    key={category}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/homepage/scenarios?contract_ids=${contract.contract_id}`);
                                    }}
                                  >
                                    <ValuePoolTag 
                                      category={category} 
                                      count={contract.category_statistics[category].scenarios_count}
                                      contractId={contract.contract_id}
                                    />
                                  </Box>
                                ))
                              : 
                              <Typography variant="caption" color="text.secondary">No categories</Typography>
                            }
                          </Box>
                        </TableCell>
                      )}
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {contract.category_statistics && 
                         contract.category_statistics['Compliance'] ? (
                          (() => {
                            const findingsCount = contract.category_statistics['Compliance'].findings_count || 0;
                            const notFoundCount = contract.category_statistics['Compliance'].not_found_count || 0;
                            const total = findingsCount + notFoundCount;
                            
                            return findingsCount > 0 || notFoundCount > 0 ? (
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center', 
                                  gap: 0.5,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.8,
                                    textDecoration: 'underline'
                                  }
                                }}
                                onClick={() => navigateToOpportunities(contract.contract_id)}
                              >
                                <WarningIcon color="warning" fontSize="small" />
                                <Typography>{findingsCount}/{total}</Typography>
                              </Box>
                            ) : null;
                          })()
                        ) : null}
                      </TableCell>
                      {columnSettings.valueLeakage && (
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {contract.category_statistics ? (
                            (() => {
                              // Calculate total findings (excluding Compliance category)
                              const findingsCount = Object.entries(contract.category_statistics)
                                .filter(([category]) => category !== 'Compliance')
                                .reduce((sum, [_, stats]) => sum + (stats as { findings_count: number }).findings_count, 0);
                              
                              // Calculate total not found count
                              const notFoundCount = Object.entries(contract.category_statistics)
                                .reduce((sum, [_, stats]) => sum + ((stats as any).not_found_count || 0), 0);
                              
                              // Total is the sum of findings and not found
                              const total = findingsCount + notFoundCount;
                              
                              return findingsCount > 0 ? (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      opacity: 0.8,
                                      textDecoration: 'underline'
                                    }
                                  }}
                                  onClick={() => navigateToOpportunities(contract.contract_id)}
                                >
                                  <WarningIcon color="warning" fontSize="small" />
                                  <Typography>{findingsCount}/{total}</Typography>
                                </Box>
                              ) : null;
                            })()
                          ) : null}
                        </TableCell>
                      )}
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <MoreOptionsMenu 
                          contract={contract} 
                          onRunAnalysis={handleRunAnalysisForContract} 
                        />
                      </TableCell>
                    </TableRow>
                    {/* Expandable content row */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnSettings.valuePool && columnSettings.valueLeakage ? 9 : columnSettings.valuePool || columnSettings.valueLeakage ? 8 : 7}>
                        <Collapse in={expandedRows[contract.contract_id.toString()]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2, bgcolor: '#f9f9f9', p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Scenarios
                            </Typography>
                            {contract.scenarios && contract.scenarios.length > 0 ? (
                              <Box sx={{ mt: 2 }}>
                                {contract.scenarios.map((scenario: any) => (
                                  <Box 
                                    key={scenario.scenario_id}
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      p: 1, 
                                      borderBottom: '1px solid #eee',
                                      '&:last-child': { borderBottom: 'none' }
                                    }}
                                  >
                                    {/* Status icon */}
                                    <Box sx={{ mr: 2 }}>
                                      {scenario.finding_status === 'No_opportunity' || 
                                       scenario.finding_status === 'No_opportunity'.toLowerCase() ? (
                                        <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                                          <CheckCircleIcon color="success" />
                                        </Box>
                                      ) : (
                                        <Box sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                                          <WarningIcon color="warning" />
                                        </Box>
                                      )}
                                    </Box>
                                    
                                    {/* Scenario details */}
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="subtitle1">
                                        {scenario.scenario_name}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {scenario.category || 'Uncategorized'}
                                      </Typography>
                                    </Box>
                                    
                                    {/* View details button */}
                                    <Button 
                                      size="small" 
                                      variant="outlined" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Navigate to the specific opportunity finding
                                        navigate(`/homepage/opportunities/${scenario.opportunity_finding_id}`);
                                      }}
                                    >
                                      View Details
                                    </Button>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body1" color="text.secondary">
                                No scenarios have been run against this contract yet.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            sx={{ mb: 3 }}
          />
        </TableContainer>
      </Grid>

      {/* Run Scenario Modal */}
      <RunScenarioModal
        open={openRunScenarioModal}
        onClose={handleCloseModal}
        workspaceID={selectedWorkspaceID}
        workspaceName={selectedWorkspaceName}
      />
    </Grid>
  );
};

// More Options Menu Component
const MoreOptionsMenu = ({ contract, onRunAnalysis }: { contract: any, onRunAnalysis: (contract: any) => void }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleRunAnalysis = () => {
    onRunAnalysis(contract);
    handleClose();
  };
  
  return (
    <div>
      <IconButton size="small" onClick={handleClick}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'more-options-button',
        }}
      >
        <MenuItem onClick={handleRunAnalysis}>
          <PlayArrowIcon fontSize="small" sx={{ mr: 1 }} />
          Run Analysis
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ContractReview;
